import React, { FC, useCallback, useMemo } from "react";
import { merge, css } from "glamor";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import InfoIcon from "@material-ui/icons/Info";
import { formatMessage } from "lib/globalize-util";
import { themeOptions } from "styles/theme";
import type { IScope } from "reducers/scope";
import { IItemRecord } from "records/item/item";
import { ProductSearch } from "components/product-search/product-search";
import { ICart } from "constants/cart";
import { getOrder } from "../../build-order";

interface IQuickAddPopupProps {
    isOpen: boolean;
    cart: ICart;
    onClosePopup: () => void;
    onCartAdd: (params: any) => Promise<any>;
    isWorkingOrder: boolean;
}
interface IState {
    scope: IScope;
    productsMap: { [id: string]: IItemRecord };
    productCollection: {
        results: string[];
        isFetching: boolean;
        hasMoreResults: boolean;
        nextPage: string;
    };
}
interface IDispatch {
    onFetchProducts: (filters: { [key: string]: string | number }) => void;
    onFetchProductsNextPage: (nextPage: string) => void;
}

const PRODUCT_FIELDS = "_id,style_number,color,name,__schema_id,__images,__media";
export const SNACK_ID = "snackbar-product-added";

export const QuickAddPopup: FC<IQuickAddPopupProps & IState & IDispatch> = (props) => {
    const { isOpen, cart, scope, productsMap, productCollection, isWorkingOrder } = props;
    const { onFetchProducts, onFetchProductsNextPage, onCartAdd, onClosePopup } = props;
    const idsInCart = useMemo(() => [...cart.items], [cart.items]);
    const useOrderWarehouse = useMemo(
        () => scope?.brand?.hasPreference("set_warehouse_for_linesheets"),
        [scope?.brand],
    );
    const handleSelectItem = useCallback(
        (productItem: IItemRecord | null) => onCartAdd({ item: productItem, params: { isCartOpen: isWorkingOrder } }),
        [onCartAdd, isWorkingOrder],
    );

    const handleFetchProducts = useCallback(
        (filters) => {
            const order = getOrder(cart);
            if (useOrderWarehouse) {
                filters.__warehouses = order.selected_warehouse ? [order.selected_warehouse] : undefined;
            }
            filters.__company = order?.retailer?.ref;
            onFetchProducts({ ...filters, __populate: "__hero_images" });
        },
        [useOrderWarehouse, cart, onFetchProducts],
    );

    const classes = useStyles();
    return (
        <div {...merge(styles.base, isOpen && styles.open)}>
            <div style={styles.label}>
                <h3 {...css(styles.title)}>
                    {formatMessage("Quick Add")}
                    <Tooltip
                        placement="top"
                        title={formatMessage("Search and select a product to add to your cart")}
                        classes={{ tooltip: classes.customStyle }}
                    >
                        <InfoIcon classes={{ root: classes.infoIcon }} />
                    </Tooltip>
                    <IconButton classes={{ root: classes.closeIcon }} onClick={onClosePopup}>
                        <CloseIcon />
                    </IconButton>
                </h3>
                <ProductSearch
                    scope={scope}
                    cart={cart}
                    idsInCart={idsInCart}
                    placeholder={formatMessage("Search to add product to your cart")}
                    productsMap={productsMap}
                    onSelectProduct={handleSelectItem}
                    productCollection={productCollection}
                    onFetch={handleFetchProducts}
                    closeOnSelect={true}
                    onFetchNextPage={onFetchProductsNextPage}
                    searchFields={PRODUCT_FIELDS}
                    autoComplete="off"
                    calculatePrices={false}
                />
            </div>
        </div>
    );
};

const styles = {
    base: {
        position: "absolute",
        display: "block",
        top: "100%",
        right: 0,
        width: 350,
        height: 150,
        background: "white",
        opacity: 0,
        zIndex: 12,
        transition: "all .3s ease-in",
        boxShadow: "0px 4px 12px rgba(0,0,0,.4)",
        "@media(max-width: 590px)": {
            right: "-80px",
            width: "270px",
        },
    },
    open: {
        maxHeight: 550,
        opacity: 1,
    },
    label: {
        display: "block",
        position: "relative" as any,
        width: "100%",
        margin: 0,
        padding: "12px 14px 0",
    },
    title: {
        fontSize: 24,
        paddingBottom: 30,
        padding: "8px 0",
        margin: 0,
        color: themeOptions.palette.darkestGrey,
    },
    message: {
        fontSize: 14,
        fontFamily: themeOptions.palette.fontFamily,
    },
};

const useStyles = makeStyles(() =>
    createStyles({
        customStyle: {
            fontSize: 11,
        },
        closeIcon: {
            bottom: 8,
            float: "right",
        },
        infoIcon: {
            marginLeft: 5,
            color: themeOptions.palette.grey,
        },
        snackBarAction: {
            fontSize: 14,
            fontFamily: themeOptions.palette.fontFamily,
            textTransform: "none",
            color: themeOptions.palette.accent1Color,
        },
    }),
);
